import type {
    UpdateWorkOrderChangeOtherPriceItemInput,
    UpdateWorkOrderOtherSeasonPriceItemInput,
    WorkOrderChangeOtherCostItem,
    WorkOrderChangeOtherObjectInput,
} from 'gql/graphql';
import { OtherCostCalculationTypeEnum } from 'gql/graphql';
import type {
    ICheckedObjs,
    IFormValues,
    IOtherNoteValues,
    IOtherValues,
} from 'pages/Project/Common/AddWorkOrderOtherForm/helpers';
import { replacerCost } from 'utils/replacerCost';

export const getInitialState = (data: Partial<WorkOrderChangeOtherCostItem>[]) =>
    data?.reduce(
        (acc: IFormValues, item) => {
            const objCheckedState = item?.workOrderChangeObjects?.reduce((objAcc: Record<any, any>, o) => {
                objAcc[`${o?.rootObjectId?.workOrderObjectId}_${o?.rootObjectId?.workOrderChangeObjectId}`] = Boolean(
                    o?.isIncluded,
                );

                return objAcc;
            }, {});
            const allObjectsChecked = item?.workOrderChangeObjects?.every((o) => o.isIncluded);
            const someObjectsChecked = item?.workOrderChangeObjects?.some((o) => o.isIncluded);
            // eslint-disable-next-line
            const parentState = allObjectsChecked ? true : someObjectsChecked ? 'intermediate' : false;

            const otherSeasonPriceValues = item?.seasonLimits?.reduce((seasonAcc: Record<any, any>, season) => {
                seasonAcc[`season_${season.workOrderChangeSeasonId}`] =
                    item?.otherCost?.calculatingType === OtherCostCalculationTypeEnum.Percent
                        ? {
                              isIncluded: season?.isIncluded,
                          }
                        : {
                              includedCost: season?.includedCost,
                          };

                return seasonAcc;
            }, {});

            const notes = item?.workOrderChangeObjects?.reduce((notesAcc: Record<any, any>, o) => {
                notesAcc[`${o?.rootObjectId?.workOrderObjectId}_${o?.rootObjectId?.workOrderChangeObjectId}`] =
                    o?.rationale;

                return notesAcc;
            }, {});

            acc.otherPriceNotes = {
                ...acc.otherPriceNotes,
                [item?.otherCost?.contractOtherCostId]: {
                    ...notes,
                },
            };

            acc.checkedIds = {
                ...acc.checkedIds,
                [item?.otherCost?.contractOtherCostId]: {
                    parent: parentState,
                    ...objCheckedState,
                },
            };

            acc.otherPriceValues = {
                ...acc.otherPriceValues,
                [item?.otherCost?.contractOtherCostId]: {
                    ...otherSeasonPriceValues,
                },
            };

            return acc;
        },
        { otherPriceValues: {}, checkedIds: {}, otherPriceNotes: {} },
    );

export const prepareOtherCostData = ({
    checkedObjects,
    priceValues,
    priceNotes,
}: {
    checkedObjects: ICheckedObjs;
    priceValues: IOtherValues;
    priceNotes?: IOtherNoteValues;
}): UpdateWorkOrderChangeOtherPriceItemInput[] =>
    Object.entries(checkedObjects).reduce(
        (acc: UpdateWorkOrderChangeOtherPriceItemInput[], [otherCostId, otherCostValue]) => {
            // Если не выбрана прочая, не формируем объект для сезонов
            if (!otherCostValue.parent) {
                return acc;
            }

            // Список айдишек выбранных объектов
            const otherObjectList =
                otherCostValue &&
                Object.entries(otherCostValue).reduce(
                    (objListAcc: WorkOrderChangeOtherObjectInput[], [objId, objValue]) => {
                        if (objValue && objId !== 'parent') {
                            // objId = `workOrderObjectId_workOrderChangeObjectId`
                            const [workOrderObjectId, workOrderChangeObjectId] = objId.split('_');

                            if (priceNotes?.[otherCostId]?.[objId]) {
                                objListAcc.push({
                                    objectId: {
                                        workOrderObjectId: Number(workOrderObjectId),
                                        workOrderChangeObjectId: Number(workOrderChangeObjectId),
                                    },
                                    rationale: priceNotes[otherCostId][objId],
                                });
                            } else {
                                objListAcc.push({
                                    objectId: {
                                        workOrderObjectId: Number(workOrderObjectId),
                                        workOrderChangeObjectId: Number(workOrderChangeObjectId),
                                    },
                                });
                            }
                        }

                        return objListAcc;
                    },
                    [],
                );

            // Список значений для сезонов
            const seasonsList =
                priceValues?.[otherCostId] &&
                Object.entries(priceValues[otherCostId]).reduce(
                    (seasonsListAcc: UpdateWorkOrderOtherSeasonPriceItemInput[], [seasonId, seasonValue]) => {
                        // seasonId - season_ID
                        if (seasonValue) {
                            const seasonNumberId = Number(seasonId.split('_')[1]);

                            seasonsListAcc.push({
                                seasonId: Number(seasonNumberId),
                                ...(seasonValue?.includedCost
                                    ? { includedCost: replacerCost(seasonValue.includedCost) }
                                    : { isIncluded: seasonValue.isIncluded }),
                            });
                        }

                        return seasonsListAcc;
                    },
                    [],
                );

            acc.push({
                otherCostId: Number(otherCostId),
                otherCostObjects: otherObjectList,
                otherSeasonPrices: seasonsList ?? [],
            });

            return acc;
        },
        [],
    );
